import { z } from "zod";
import { productSpecsSchema, sizeGuideSchema } from "./catalog-storefront.js";
import { defineEndpoint } from "./endpoint.js";
/**
 * The online store (docs/specs/storefront.md, Phase 4, PRD SF-01 … SF-20). B-72: the merchant's storefront settings
 * and the public "which shop is this host" lookup the Next.js storefront starts every page with.
 */
const tag = { tag: "storefront" };
const view = { permission: "storefront.view" };
const manage = { permission: "storefront.manage" };
/** A storefront host as the browser sent it (`shop.example.com` or `demo.localhost:3100`). */
export const storefrontHostSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1)
    .max(253)
    .regex(/^[a-z0-9.-]+(:\d{1,5})?$/);
export const storefrontSettingsSchema = z.object({
    /** The Owner turned the shop on (it is live only while the plan has `feature.storefront`). */
    enabled: z.boolean(),
    /** The plan includes the storefront. */
    available: z.boolean(),
    /** `https://{slug}.<platform domain>`. */
    shopUrl: z.string(),
    /**
     * Branches online orders take stock from (owner decision 2026-10-30). Empty: the default online-order location
     * (L-03).
     */
    onlineLocationIds: z.array(z.uuid()),
    /** Show "3 left" instead of only in stock / low / out (SF-05). */
    showStockCount: z.boolean(),
    /** Cash on delivery offered at checkout (SF-09). */
    codEnabled: z.boolean(),
    /** Shown in the shop's header/footer; E.164. */
    contactPhone: z.string().nullable(),
    /** B-87: which new reviews go live without moderation (`good` = 4 and 5 stars). */
    reviewAutoPublish: z.enum(["none", "good", "all"]),
    contactEmail: z.string().nullable(),
    /**
     * Shoppers prove their phone with an SMS code before ordering. Off: they order with the phone number alone (bot
     * check and per-phone limits still apply) and cash-on-delivery orders wait for the shop's confirmation call.
     */
    checkoutOtp: z.boolean().default(true),
    /**
     * ST-11 (A9): show "10+ sold in the last 30 days" (buckets 10+ / 50+ / 100+ from delivered orders) on products.
     * Off by default; never a number the orders do not support.
     */
    showSoldCount: z.boolean().default(false),
    /** DG-01 (ACC-03): shoppers may sign up with an email / a mobile number. */
    signUpEmail: z.boolean().default(true),
    signUpPhone: z.boolean().default(true),
    /** DG-01 (ACC-03): sign-up asks for a code sent to the email / the mobile number first (off by default). */
    signUpEmailCode: z.boolean().default(false),
    signUpPhoneCode: z.boolean().default(false),
    /** DG-01 (ACC-02): mobile accounts may also sign in with an SMS code instead of the password. */
    phoneCodeSignIn: z.boolean().default(false),
    /** DG-02 (ACC-04): the shop's own Google OAuth web client ID; null: no Google sign-in. */
    googleClientId: z.string().nullable().default(null),
    /** B-84: the platform closed the shop (abuse, phishing); only the platform can reopen it. */
    takenDown: z.boolean().default(false),
    /** PL-07: hide "Powered by Sobthik" in the shop; it shows again while the plan lacks `feature.remove_branding`. */
    hidePoweredBy: z.boolean().default(false),
    /** 0 = never saved. */
    version: z.int(),
});
/** `123456789012-abc123.apps.googleusercontent.com` (DG-02). */
export const googleClientIdSchema = z
    .string()
    .trim()
    .max(200)
    .regex(/^[0-9]+-[a-z0-9]+\.apps\.googleusercontent\.com$/, "Paste the Client ID ending in .apps.googleusercontent.com");
export const updateStorefrontSettingsSchema = z
    .object({
    enabled: z.boolean().optional(),
    onlineLocationIds: z.array(z.uuid()).max(50).optional(),
    showStockCount: z.boolean().optional(),
    codEnabled: z.boolean().optional(),
    contactPhone: z.string().trim().max(32).nullable().optional(),
    contactEmail: z.email().max(254).nullable().optional(),
    reviewAutoPublish: z.enum(["none", "good", "all"]).optional(),
    checkoutOtp: z.boolean().optional(),
    showSoldCount: z.boolean().optional(),
    signUpEmail: z.boolean().optional(),
    signUpPhone: z.boolean().optional(),
    signUpEmailCode: z.boolean().optional(),
    signUpPhoneCode: z.boolean().optional(),
    phoneCodeSignIn: z.boolean().optional(),
    /** DG-02: a Google "Web application" client ID; null removes Google sign-in. */
    googleClientId: googleClientIdSchema.nullable().optional(),
    hidePoweredBy: z.boolean().optional(),
    version: z.int().min(0),
})
    .strict();
export const getStorefrontSettings = defineEndpoint({
    ...tag,
    name: "getStorefrontSettings",
    method: "GET",
    path: "/v1/storefront-settings",
    access: view,
    summary: "The online store's settings",
    status: 200,
    response: storefrontSettingsSchema,
});
export const updateStorefrontSettings = defineEndpoint({
    ...tag,
    name: "updateStorefrontSettings",
    method: "PATCH",
    path: "/v1/storefront-settings",
    access: manage,
    summary: "Turn the online store on or off and change its settings",
    body: updateStorefrontSettingsSchema,
    status: 200,
    response: storefrontSettingsSchema,
    errors: ["CONFLICT", "FEATURE_NOT_IN_PLAN", "LOCATION_INACTIVE"],
});
// Public: the storefront server (Next.js) asks which shop a host belongs to ----------------------------------------
export const storefrontShopQuerySchema = z.object({ host: storefrontHostSchema });
export const storefrontShopSchema = z.object({
    slug: z.string(),
    /**
     * The shop's main address (`shop.example.com`, or the platform address with its port in development). A request
     * on any other host of the same shop is sent here with a 301 (SF-02).
     */
    canonicalHost: z.string(),
    /** `https:` or `http:` for links to the canonical host. */
    canonicalProtocol: z.enum(["https:", "http:"]),
    name: z.string(),
    /** The shop's language for shoppers (`customer_locale`); shoppers may switch. */
    locale: z.enum(["en", "bn"]),
    currency: z.literal("BDT"),
    contactPhone: z.string().nullable(),
    contactEmail: z.string().nullable(),
    codEnabled: z.boolean(),
    showStockCount: z.boolean(),
    /** Checkout asks for an SMS code (the shop's setting). */
    checkoutOtp: z.boolean().default(true),
    /** DG-01: how shoppers sign up and sign in at this shop (the shop's settings). */
    accounts: z
        .object({
        signUpEmail: z.boolean(),
        signUpPhone: z.boolean(),
        signUpEmailCode: z.boolean(),
        signUpPhoneCode: z.boolean(),
        phoneCodeSignIn: z.boolean(),
        /** DG-02: the shop's Google client ID for the "Continue with Google" button; null: no button. */
        googleClientId: z.string().nullable().default(null),
    })
        .default({
        signUpEmail: true,
        signUpPhone: true,
        signUpEmailCode: false,
        signUpPhoneCode: false,
        phoneCodeSignIn: false,
        googleClientId: null,
    }),
    /**
     * ST-07 (A6): delivery is free from this subtotal (paisa) for every shopper — the lowest minimum order of the shop's
     * automatic free-delivery coupons that apply to all products without per-phone limits; null: no such offer.
     */
    freeDeliveryFromPaisa: z.int().nullable().default(null),
    /** PL-07: show "Powered by Sobthik" in the footer (false only when the shop hid it and its plan allows). */
    poweredBy: z.boolean().default(true),
    /** ST-25: a theme demo shop the platform built: a banner says so and orders answer 422 `DEMO_SHOP`. */
    demo: z.boolean().default(false),
});
export const getStorefrontShop = defineEndpoint({
    ...tag,
    name: "getStorefrontShop",
    method: "GET",
    path: "/v1/storefront/shop",
    access: { public: true },
    summary: "The shop a storefront host belongs to (404 when there is none or it is closed)",
    query: storefrontShopQuerySchema,
    status: 200,
    response: storefrontShopSchema,
});
// Own domains (B-73, SF-02): Cloudflare for SaaS custom hostnames -----------------------------------------------
export const DOMAIN_STATUSES = ["pending_dns", "verifying", "active", "failed", "blocked"];
/** A host name the merchant owns, e.g. `shop.example.com` (lower-case; Bengali/IDN names are sent as typed). */
export const domainHostnameSchema = z.string().trim().min(4).max(253);
export const dnsRecordSchema = z.object({
    type: z.enum(["CNAME", "TXT"]),
    name: z.string(),
    value: z.string(),
});
export const storefrontDomainSchema = z.object({
    id: z.uuid(),
    hostname: z.string(),
    status: z.enum(DOMAIN_STATUSES),
    /** Shoppers on any other address of the shop are sent here (301). */
    isPrimary: z.boolean(),
    /** What the merchant sets at their DNS provider. */
    records: z.array(dnsRecordSchema),
    /** Why it failed or was blocked, in plain words. */
    problem: z.string().nullable(),
    lastCheckedAt: z.iso.datetime().nullable(),
    activeSince: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    version: z.int(),
});
export const storefrontDomainListSchema = z.object({
    data: z.array(storefrontDomainSchema),
    /** The platform address, always serving (redirects to the primary domain when there is one). */
    platformHost: z.string(),
});
export const addStorefrontDomainSchema = z.object({ hostname: domainHostnameSchema }).strict();
const domainParams = z.object({ id: z.uuid() });
export const listStorefrontDomains = defineEndpoint({
    ...tag,
    name: "listStorefrontDomains",
    method: "GET",
    path: "/v1/storefront-domains",
    access: view,
    summary: "The shop's own domains and their status",
    status: 200,
    response: storefrontDomainListSchema,
});
export const addStorefrontDomain = defineEndpoint({
    ...tag,
    name: "addStorefrontDomain",
    method: "POST",
    path: "/v1/storefront-domains",
    access: manage,
    summary: "Connect a domain the business owns (then set the DNS records shown)",
    body: addStorefrontDomainSchema,
    status: 201,
    response: storefrontDomainSchema,
    entitlement: "feature.custom_domain",
    errors: ["FEATURE_NOT_IN_PLAN", "DOMAIN_TAKEN", "DOMAIN_LIMIT_REACHED"],
});
export const checkStorefrontDomain = defineEndpoint({
    ...tag,
    name: "checkStorefrontDomain",
    method: "POST",
    path: "/v1/storefront-domains/{id}/check",
    access: manage,
    summary: "Check the DNS records again now",
    params: domainParams,
    status: 202,
    response: storefrontDomainSchema,
});
export const makeStorefrontDomainPrimary = defineEndpoint({
    ...tag,
    name: "makeStorefrontDomainPrimary",
    method: "POST",
    path: "/v1/storefront-domains/{id}/primary",
    access: manage,
    summary: "Send shoppers on every other address to this domain",
    params: domainParams,
    status: 200,
    response: storefrontDomainSchema,
    errors: ["INVALID_TRANSITION"],
});
export const clearStorefrontPrimaryDomain = defineEndpoint({
    ...tag,
    name: "clearStorefrontPrimaryDomain",
    method: "POST",
    path: "/v1/storefront-domains/primary/clear",
    access: manage,
    summary: "Use the platform address as the main address again",
    status: 204,
});
export const removeStorefrontDomain = defineEndpoint({
    ...tag,
    name: "removeStorefrontDomain",
    method: "DELETE",
    path: "/v1/storefront-domains/{id}",
    access: manage,
    summary: "Disconnect a domain (it stops serving the shop)",
    params: domainParams,
    status: 204,
});
// Super Admin: every shop's domains, block on abuse (B-73) ----------------------------------------------------------
export const platformDomainSchema = storefrontDomainSchema.extend({
    tenantId: z.uuid(),
    businessName: z.string(),
});
export const platformDomainQuerySchema = z.object({
    status: z.enum(DOMAIN_STATUSES).optional(),
    q: z.string().trim().max(253).optional(),
});
export const listPlatformDomains = defineEndpoint({
    tag: "platform-admin",
    name: "listPlatformDomains",
    method: "GET",
    path: "/v1/platform/domains",
    access: { platformAdmin: true },
    summary: "Own domains of every shop (latest 200), filtered by status or host",
    query: platformDomainQuerySchema,
    status: 200,
    response: z.object({ data: z.array(platformDomainSchema) }),
});
export const blockPlatformDomainSchema = z
    .object({ tenantId: z.uuid(), reason: z.string().trim().min(3).max(500) })
    .strict();
export const blockPlatformDomain = defineEndpoint({
    tag: "platform-admin",
    name: "blockPlatformDomain",
    method: "POST",
    path: "/v1/platform/domains/{id}/block",
    access: { platformAdmin: true },
    summary: "Stop a domain serving any shop (abuse, phishing); audited",
    params: domainParams,
    body: blockPlatformDomainSchema,
    status: 200,
    response: platformDomainSchema,
});
// Public catalog (B-74, SF-04 … SF-06): what the storefront server reads for a host ---------------------------------
export const AVAILABILITY = ["in_stock", "low", "out"];
/**
 * ST-11 (A9): delivered orders with the product in the last 30 days, rounded down to 10+ / 50+ / 100+ (null below 10
 * or when the shop keeps the switch off). Counts orders, not pieces, so it never overstates.
 */
export const SOLD_BUCKETS = [10, 50, 100];
export const soldBucketSchema = z
    .union([z.literal(10), z.literal(50), z.literal(100)])
    .nullable()
    .default(null);
/** A public image: CDN URLs of the resized copies (null when public images are not configured). */
export const publicImageSchema = z.object({
    thumb: z.url().nullable(),
    medium: z.url().nullable(),
    large: z.url().nullable(),
});
export const shopCategorySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    parentId: z.uuid().nullable(),
    /** Published products in it and its sub-categories. */
    productCount: z.int(),
});
/** ST-16: the price of one kilogram or litre, when the variant's label is a plain net amount ("500 g", "2 L"). */
export const unitPriceSchema = z.object({ pricePaisa: z.int(), per: z.enum(["kg", "l"]) });
/**
 * ST-16 (grocery cards): one buyable choice of a product whose variants differ by a single non-colour option (Weight,
 * Size …), or its only variant (label ""). Lets a card add the chosen weight to the cart without opening the product.
 */
export const cardChoiceSchema = z.object({
    variantId: z.uuid(),
    label: z.string(),
    pricePaisa: z.int(),
    mrpPaisa: z.int().nullable(),
    availability: z.enum(AVAILABILITY),
    /** Null when the label is not a net amount, or is exactly 1 kg / 1 L (the price already says it). */
    unitPrice: unitPriceSchema.nullable(),
});
export const shopProductCardSchema = z.object({
    id: z.uuid(),
    slug: z.string(),
    name: z.string(),
    image: publicImageSchema.nullable(),
    /** Lowest and highest price of its sellable variants (paisa). */
    minPricePaisa: z.int(),
    maxPricePaisa: z.int(),
    /** The MRP struck through when above the lowest price. */
    mrpPaisa: z.int().nullable(),
    availability: z.enum(AVAILABILITY),
    categoryId: z.uuid().nullable(),
    /** ST-06 (A1): the second product image, shown on hover / swipe. */
    hoverImage: publicImageSchema.nullable().default(null),
    /** Average of published reviews (one decimal) and how many; null / 0 without reviews (B-87). */
    ratingAverage: z.number().min(1).max(5).nullable().default(null),
    ratingCount: z.int().min(0).default(0),
    /** Colour values (option named colour/color/রং/রঙ), in option order, with a variant picture when there is one. */
    swatches: z
        .array(z.object({ value: z.string(), image: publicImageSchema.nullable() }))
        .max(6)
        .default([]),
    /** `new`: published in the last 14 days; `sale`: below MRP; `low_stock`: few left (never a number, PRD §8). */
    badges: z.array(z.enum(["new", "sale", "low_stock"])).default([]),
    /** The first other option (Size, Weight …) with the values that have an active variant, for quick add. */
    // Defaults: a storefront release ahead of the API (or its 60 s data cache) still reads a complete card.
    optionSummary: z
        .object({ name: z.string(), values: z.array(z.string()) })
        .nullable()
        .default(null),
    soldBucket: soldBucketSchema,
    /**
     * ST-15 (A4 on cards): the first two specification values, in the merchant's order ("40 h battery", "ANC") — the
     * chips a gadget card shows; empty without specifications.
     */
    keySpecs: z.array(z.string()).max(2).default([]),
    /** ST-16: up to four choices in option order; empty when the product has more options, more variants or none. */
    choices: z.array(cardChoiceSchema).max(4).default([]),
    /** DG-05: it can be bought as an eBook (a digital variant); cards show an "eBook" tag. */
    digital: z.boolean().default(false),
    /** DG-08: the book's writers (role `author`, in order, at most two) — book cards name and link them. */
    authors: z
        .array(z.object({ name: z.string(), slug: z.string() }))
        .max(2)
        .default([]),
});
/**
 * ST-19: an automatic offer every shopper gets (no code, no limit, valid now) — "10% off orders from ৳3,000". The
 * order applies it by itself; the storefront only points to it.
 */
export const shopOfferSchema = z.object({
    kind: z.enum(["percent", "amount"]),
    percentBp: z.int().nullable(),
    amountPaisa: z.int().nullable(),
    /** `percent`: the most it takes off (null: no cap). */
    maxDiscountPaisa: z.int().nullable(),
    minOrderPaisa: z.int(),
});
export const shopVariantSchema = z.object({
    id: z.uuid(),
    optionValues: z.record(z.string(), z.string()),
    pricePaisa: z.int(),
    mrpPaisa: z.int().nullable(),
    availability: z.enum(AVAILABILITY),
    /** Only when the shop shows stock counts (decimal string in the unit, like the Admin). */
    stockCount: z.string().nullable(),
    image: publicImageSchema.nullable(),
    /** ST-16: price per kilogram / litre when the variant's single option value is a net amount (see card choices). */
    unitPrice: unitPriceSchema.nullable().default(null),
    /** DG-05: a digital variant (an eBook) is never shipped and has no stock. */
    fulfilment: z.enum(["physical", "digital"]).default("physical"),
    digitalKind: z.enum(["ebook"]).nullable().default(null),
    /** DG-07: an eBook whose watermarked sample is ready — "Read a little" opens it, signed out too. */
    hasSample: z.boolean().default(false),
});
export const shopProductSchema = z.object({
    id: z.uuid(),
    /** The current address; the storefront redirects when the request used an old one. */
    slug: z.string(),
    name: z.string(),
    /** Safe HTML (ADR-0016). */
    description: z.string(),
    seoTitle: z.string(),
    seoDescription: z.string(),
    images: z.array(publicImageSchema),
    options: z.array(z.object({ name: z.string(), values: z.array(z.string()) })),
    variants: z.array(shopVariantSchema),
    category: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    brandName: z.string().nullable(),
    /** ST-07 (A4): specifications and size chart (defaults when the API is a release behind). */
    specs: productSpecsSchema.default([]),
    sizeGuide: sizeGuideSchema.nullable().default(null),
    unitCode: z.string(),
    soldBucket: soldBucketSchema,
    updatedAt: z.iso.datetime(),
    /** ST-19: the automatic offer that covers this product, lowest minimum order first; null: none. */
    offer: shopOfferSchema.nullable().default(null),
    /** DG-05 (BK-01): the book's authors in order, and its details. */
    authors: z
        .array(z.object({ name: z.string(), slug: z.string(), role: z.enum(["author", "translator", "editor"]) }))
        .default([]),
    book: z
        .object({
        isbn: z.string(),
        pages: z.int().nullable(),
        edition: z.string(),
        language: z.string(),
        publishedYear: z.int().nullable(),
    })
        .nullable()
        .default(null),
});
export const SHOP_SORTS = ["newest", "price_asc", "price_desc", "name"];
export const shopProductListQuerySchema = z.object({
    host: storefrontHostSchema,
    categoryId: z.uuid().optional(),
    q: z.string().trim().max(100).optional(),
    minPricePaisa: z.coerce.number().int().min(0).optional(),
    maxPricePaisa: z.coerce.number().int().min(0).optional(),
    inStock: z
        .enum(["true", "false"])
        .optional()
        .transform((value) => value === "true"),
    /**
     * ST-06 (A2): option filters as `Name:Value`, repeated (`opt=Size:M&opt=Colour:Red`): values of one option are
     * alternatives, different options must all match.
     */
    opt: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .transform((v) => (v === undefined ? [] : Array.isArray(v) ? v : [v]))
        .pipe(z
        .array(z
        .string()
        .trim()
        .regex(/^[^:]{1,40}:[^:]{1,60}$/))
        .max(12)),
    brandId: z.uuid().optional(),
    /** DG-08 (BK-01): one author's books (the author's address). */
    author: z.string().trim().min(1).max(160).optional(),
    /** DG-08: hard copies or digital products (a product with both formats matches both). */
    format: z.enum(["physical", "digital"]).optional(),
    sort: z.enum(SHOP_SORTS).default("newest"),
    limit: z.coerce.number().int().min(1).max(48).default(24),
    /** Opaque, from `nextCursor`. */
    cursor: z.string().max(200).optional(),
});
export const shopProductListSchema = z.object({
    data: z.array(shopProductCardSchema),
    nextCursor: z.string().nullable(),
});
export const listShopCategories = defineEndpoint({
    ...tag,
    name: "listShopCategories",
    method: "GET",
    path: "/v1/storefront/categories",
    access: { public: true },
    summary: "Categories with published products, for the shop's menu",
    query: storefrontShopQuerySchema,
    status: 200,
    response: z.object({ data: z.array(shopCategorySchema) }),
});
export const listShopProducts = defineEndpoint({
    ...tag,
    name: "listShopProducts",
    method: "GET",
    path: "/v1/storefront/products",
    access: { public: true },
    summary: "Published products: by category, search, price range, in stock; sorted; cursor paging",
    query: shopProductListQuerySchema,
    status: 200,
    response: shopProductListSchema,
    errors: ["SEARCH_UNAVAILABLE"],
});
/** ST-07 (A5): products to show with one: related (same category, newest first) and the merchant's picks. */
export const shopRelatedSchema = z.object({
    related: z.array(shopProductCardSchema).max(8),
    /** "Pairs well with" / "Complete the look", in the merchant's order (published, in stock or not). */
    pairs: z.array(shopProductCardSchema).max(8),
    /**
     * The price of this product's cheapest in-stock variant plus each in-stock pick's, when there is at least one
     * pick (server sum, `packages/domain`); null otherwise.
     */
    bundleTotalPaisa: z.int().nullable(),
});
export const getShopRelated = defineEndpoint({
    ...tag,
    name: "getShopRelated",
    method: "GET",
    path: "/v1/storefront/products/{slug}/related",
    access: { public: true },
    summary: "Related products and the merchant's picks for a product, with the bundle total",
    params: z.object({ slug: z.string().min(1).max(200) }),
    query: storefrontShopQuerySchema,
    status: 200,
    response: shopRelatedSchema,
});
/** ST-11 (A8): a branch the merchant lists on the shop (active and marked visible only). */
export const shopLocationSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    address: z.string(),
    /** E.164, or null. */
    phone: z.string().nullable(),
    openingHours: z.string(),
    mapUrl: z.string().nullable(),
});
export const listShopLocations = defineEndpoint({
    ...tag,
    name: "listShopLocations",
    method: "GET",
    path: "/v1/storefront/locations",
    access: { public: true },
    summary: "The shop's branches marked visible on the online shop (store locator, footer)",
    query: storefrontShopQuerySchema,
    status: 200,
    response: z.object({ data: z.array(shopLocationSchema) }),
});
/** ST-07 (A6): delivery to a district — the charge of its zone (base weight) and the zone's delivery days. */
export const shopDeliveryEstimateSchema = z.object({
    chargePaisa: z.int(),
    zoneName: z.string().nullable(),
    daysMin: z.int().nullable(),
    daysMax: z.int().nullable(),
});
export const getShopDeliveryEstimate = defineEndpoint({
    ...tag,
    name: "getShopDeliveryEstimate",
    method: "GET",
    path: "/v1/storefront/delivery-estimate",
    access: { public: true },
    summary: "Delivery charge and days for a district (the order's final charge is computed at checkout)",
    query: z.object({ host: storefrontHostSchema, districtId: z.coerce.number().int().min(1).max(999) }),
    status: 200,
    response: shopDeliveryEstimateSchema,
});
/** ST-06 (A2): what the filters can offer for the products in scope (category / search / price), with counts. */
export const shopFacetsSchema = z.object({
    options: z.array(z.object({ name: z.string(), values: z.array(z.object({ value: z.string(), count: z.int() })) })),
    brands: z.array(z.object({ id: z.uuid(), name: z.string(), count: z.int() })),
    price: z.object({ minPaisa: z.int().nullable(), maxPaisa: z.int().nullable() }),
    /** DG-08 (BK-01): the authors of the products in scope (most books first) and the formats on offer. */
    authors: z.array(z.object({ slug: z.string(), name: z.string(), count: z.int() })).default([]),
    formats: z.array(z.object({ value: z.enum(["physical", "digital"]), count: z.int() })).default([]),
});
export const getShopFacets = defineEndpoint({
    ...tag,
    name: "getShopFacets",
    method: "GET",
    path: "/v1/storefront/products/facets",
    access: { public: true },
    summary: "Filter choices with counts (option values, brands, authors, formats, price) for a category or search",
    query: z.object({
        host: storefrontHostSchema,
        categoryId: z.uuid().optional(),
        q: z.string().trim().max(100).optional(),
    }),
    status: 200,
    response: shopFacetsSchema,
    errors: ["SEARCH_UNAVAILABLE"],
});
/** ST-05 (A3): what the search overlay shows while the shopper types — no free-text suggestions in v1. */
export const shopSearchSuggestSchema = z.object({
    /** Best matches first, at most 6. */
    products: z.array(shopProductCardSchema).max(6),
    /** Categories whose name contains the query, at most 4. */
    categories: z.array(shopCategorySchema).max(4),
});
export const suggestShopSearch = defineEndpoint({
    ...tag,
    name: "suggestShopSearch",
    method: "GET",
    path: "/v1/storefront/search/suggest",
    access: { public: true },
    summary: "Instant search: the best published products and matching categories for what the shopper typed",
    query: z.object({ host: storefrontHostSchema, q: z.string().trim().min(1).max(100) }),
    status: 200,
    response: shopSearchSuggestSchema,
    errors: ["SEARCH_UNAVAILABLE"],
});
export const getShopProduct = defineEndpoint({
    ...tag,
    name: "getShopProduct",
    method: "GET",
    path: "/v1/storefront/products/{slug}",
    access: { public: true },
    summary: "One published product by its address (an old address answers with the current slug)",
    params: z.object({ slug: z.string().min(1).max(200) }),
    query: storefrontShopQuerySchema,
    status: 200,
    response: shopProductSchema,
});
//# sourceMappingURL=storefront.js.map